import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ContactMail as ContactIcon,
} from '@mui/icons-material';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  contactCard: {
    marginBottom: theme.spacing(2),
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  typeSelect: {
    minWidth: 120,
  },
  valueField: {
    flexGrow: 1,
  },
  labelField: {
    minWidth: 150,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
});

type ContactType = 'phone' | 'email';

interface ContactConfig {
  label: string;
  icon: React.ElementType;
  placeholder: string;
}

const CONTACT_TYPES: Record<ContactType, ContactConfig> = {
  phone: { label: 'Phone', icon: PhoneIcon, placeholder: '(999) 999-9999' },
  email: { label: 'Email', icon: EmailIcon, placeholder: 'contact@company.com' },
};

interface InternalContact {
  id: string;
  type: ContactType;
  value: string;
  label: string;
}

interface PhoneContactData {
  phone: { number: string; label: string };
}

interface EmailContactData {
  email: { email: string; label: string };
}

type ExternalContact = PhoneContactData | EmailContactData;

interface VendorContactManagerOwnProps {
  contacts?: ExternalContact[];
  onChange?: (contacts: ExternalContact[]) => void;
}

interface VendorContactManagerProps
  extends VendorContactManagerOwnProps,
    WithStyles<typeof styles> {}

interface VendorContactManagerState {
  contacts: InternalContact[];
}

class VendorContactManager extends React.Component<
  VendorContactManagerProps,
  VendorContactManagerState
> {
  constructor(props: VendorContactManagerProps) {
    super(props);
    const contacts = Array.isArray(props.contacts) ? props.contacts : [];
    this.state = {
      contacts: this.normalizeContacts(contacts),
    };
  }

  override componentDidUpdate(prevProps: VendorContactManagerProps): void {
    if (prevProps.contacts !== this.props.contacts) {
      const contacts = Array.isArray(this.props.contacts) ? this.props.contacts : [];
      this.setState({
        contacts: this.normalizeContacts(contacts),
      });
    }
  }

  normalizeContacts = (contacts: ExternalContact[]): InternalContact[] => {
    return contacts.map((contact, index) => {
      if ('phone' in contact) {
        return {
          id: `contact_${index}`,
          type: 'phone' as ContactType,
          value: contact.phone.number || '',
          label: contact.phone.label || '',
        };
      } else if ('email' in contact) {
        return {
          id: `contact_${index}`,
          type: 'email' as ContactType,
          value: contact.email.email || '',
          label: contact.email.label || '',
        };
      }
      return {
        id: `contact_${index}`,
        type: 'phone' as ContactType,
        value: '',
        label: '',
      };
    });
  };

  denormalizeContacts = (contacts: InternalContact[]): ExternalContact[] => {
    return contacts
      .filter((contact) => contact.value.trim() !== '')
      .map((contact): ExternalContact => {
        if (contact.type === 'phone') {
          return { phone: { number: contact.value, label: contact.label || 'Phone' } };
        }
        return { email: { email: contact.value, label: contact.label || 'Email' } };
      });
  };

  handleContactChange = (contactId: string, field: string, value: string): void => {
    this.setState((prevState) => {
      const newContacts = prevState.contacts.map((contact) => {
        if (contact.id === contactId) {
          return { ...contact, [field]: value } as InternalContact;
        }
        return contact;
      });

      const denormalizedContacts = this.denormalizeContacts(newContacts);
      if (this.props.onChange) {
        this.props.onChange(denormalizedContacts);
      }

      return { contacts: newContacts };
    });
  };

  handleAddContact = (): void => {
    const newContact: InternalContact = {
      id: `contact_${Date.now()}`,
      type: 'phone',
      value: '',
      label: '',
    };

    this.setState((prevState) => ({
      contacts: [...prevState.contacts, newContact],
    }));
  };

  handleRemoveContact = (contactId: string): void => {
    this.setState((prevState) => {
      const newContacts = prevState.contacts.filter((contact) => contact.id !== contactId);

      const denormalizedContacts = this.denormalizeContacts(newContacts);
      if (this.props.onChange) {
        this.props.onChange(denormalizedContacts);
      }

      return { contacts: newContacts };
    });
  };

  validateContactValue = (type: ContactType, value: string): boolean => {
    if (!value.trim()) return true;

    if (type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    } else if (type === 'phone') {
      const phoneRegex = /^[\+]?[1-9]?[\d\s\-\(\)\.]{7,15}$/;
      return phoneRegex.test(value.replace(/\s/g, ''));
    }

    return true;
  };

  renderContactCard = (contact: InternalContact): React.JSX.Element => {
    const { classes } = this.props;
    const typeConfig = CONTACT_TYPES[contact.type];
    const IconComponent = typeConfig.icon;
    const isValid = this.validateContactValue(contact.type, contact.value);

    return (
      <Card key={contact.id} className={classes.contactCard} variant="outlined">
        <CardContent>
          <Box className={classes.contactRow}>
            <FormControl className={classes.typeSelect}>
              <InputLabel>Type</InputLabel>
              <Select
                value={contact.type}
                onChange={(e) => this.handleContactChange(contact.id, 'type', e.target.value as string)}
                label="Type"
              >
                {Object.entries(CONTACT_TYPES).map(([key, config]) => {
                  const IconComp = config.icon;
                  return (
                    <MenuItem key={key} value={key}>
                      <IconComp sx={{ marginRight: 1 }} fontSize="small" />
                      {config.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              className={classes.valueField}
              label={typeConfig.label}
              value={contact.value}
              onChange={(e) => this.handleContactChange(contact.id, 'value', e.target.value)}
              placeholder={typeConfig.placeholder}
              error={!isValid}
              helperText={!isValid ? `Invalid ${typeConfig.label.toLowerCase()} format` : ''}
              InputProps={{
                startAdornment: <IconComponent sx={{ marginRight: 1 }} color="action" />,
              }}
            />

            <TextField
              className={classes.labelField}
              label="Label"
              value={contact.label}
              onChange={(e) => this.handleContactChange(contact.id, 'label', e.target.value)}
              placeholder="e.g., Main, Support"
            />

            <IconButton
              color="error"
              onClick={() => this.handleRemoveContact(contact.id)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  override render(): React.JSX.Element {
    const { classes } = this.props;
    const { contacts } = this.state;

    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.header}>
          <ContactIcon />
          Additional Contacts
        </Typography>

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Add additional phone numbers and email addresses for this vendor.
        </Typography>

        {contacts.length === 0 ? (
          <Box className={classes.emptyState}>
            <ContactIcon fontSize="large" color="inherit" />
            <Typography variant="body1" gutterBottom>
              No additional contacts added yet
            </Typography>
            <Typography variant="body2">
              Click "Add Contact" to add phone numbers or email addresses
            </Typography>
          </Box>
        ) : (
          contacts.map(this.renderContactCard)
        )}

        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={this.handleAddContact}
          className={classes.addButton}
        >
          Add Contact
        </Button>

        {contacts.length > 0 && (
          <>
            <Divider sx={{ marginY: 2 }} />
            <Typography variant="caption" color="textSecondary">
              {contacts.filter((c) => c.value.trim()).length} contact(s) will be saved
            </Typography>
          </>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(VendorContactManager);
